/**
 * How long ago a device-local copy was taken, in the few words a dialog needs.
 *
 * Shared by both creator studios, because the question a recovery offer asks is
 * the same one in each — *is this the work I was just doing?* — and the answer has
 * to read the same way whether the copy holds a FlashBook or a Capsule. Two
 * mappings that look alike would be two that drift, and the drift would show up as
 * one studio saying "a moment ago" about an hour-old copy.
 *
 * Coarse on purpose: the offer is a decision — *mine or not mine* — and the
 * difference between "47 minutes ago" and "52 minutes ago" has never changed
 * anyone's answer. Seconds are named because a crash-and-reopen is the common
 * case, and "a moment ago" is what makes it obvious that the copy is the work you
 * were just doing.
 */
export type SnapshotAge = 'moment' | 'minutes' | 'hours' | 'days' | 'longer'

export function describeSnapshotAge(
  savedAt: string,
  now: number = Date.now(),
): SnapshotAge {
  const taken = Date.parse(savedAt)
  if (!Number.isFinite(taken)) return 'longer'
  const seconds = Math.max(0, (now - taken) / 1000)
  if (seconds < 90) return 'moment'
  if (seconds < 90 * 60) return 'minutes'
  if (seconds < 36 * 3600) return 'hours'
  if (seconds < 14 * 24 * 3600) return 'days'
  return 'longer'
}
